import { db } from '../../data/base';
import { collection, addDoc, doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { secureGetItem } from '../../data/utils';
import {
    prepareAssetsAndSave,
    mapFormDataToCatalogue,
    sanitizeForFirestore,
    updateUserDraftedCatalogues,
    addCatalogToClientRef
} from './catalogueBaseService';
import { updateDraftReference, removeDraftReference } from './catalogueService';

/**
 * Servizio unificato per la gestione dei cataloghi e delle bozze
 * Semplifica l'architettura esistente fornendo un'interfaccia coerente
 */

/**
 * Salva un catalogo o una bozza nel database
 * @param {Object} options - Opzioni per il salvataggio
 * @param {Object} options.formData - Dati del form
 * @param {boolean} options.isDraft - Se true, salva come bozza
 * @param {string} options.originalCatalogueId - ID del catalogo originale (per bozze di cataloghi pubblicati)
 * @param {string} options.existingDraftId - ID della bozza esistente (per aggiornamenti)
 * @returns {Promise<string>} - ID del catalogo o della bozza salvata
 */
export async function saveCatalogueUnified(options) {
    const {
        formData,
        isDraft = false,
        originalCatalogueId = null,
        existingDraftId = null
    } = options;

    try {
        // Caso 1: Aggiornamento di una bozza esistente
        if (isDraft && existingDraftId) {
            return updateExistingDraft(existingDraftId, formData);
        }

        // Caso 2: Creazione di una bozza da un catalogo pubblicato
        if (isDraft && originalCatalogueId) {
            return savePublishedAsDraft(originalCatalogueId, formData);
        }

        // Caso 3: Creazione di una nuova bozza
        if (isDraft) {
            return saveNewDraft(formData);
        }

        // Caso 4: Creazione/aggiornamento di un catalogo pubblicato
        return savePublishedCatalogue(formData, originalCatalogueId);

    } catch (error) {
        console.error("Error in unified catalogue service:", error);
        throw error;
    }
}

/**
 * Aggiorna una bozza esistente
 * @param {string} draftId - ID della bozza
 * @param {Object} formData - Dati del form
 * @returns {Promise<string>} - ID della bozza aggiornata
 */
async function updateExistingDraft(draftId, formData) {
    try {

        // Prepara gli asset (carica immagini, ecc.)
        const processedFormData = await prepareAssetsAndSave(formData);

        // Ottieni i dati della bozza esistente
        const draftRef = doc(db, "DraftedCatalogues", draftId);
        const draftSnap = await getDoc(draftRef);

        if (!draftSnap.exists()) {
            throw new Error(`Draft with ID ${draftId} not found`);
        }

        const draftData = draftSnap.data();

        // Mappa i dati nella struttura JSON standard
        const mappedData = mapFormDataToCatalogue(processedFormData);

        // Mantieni i campi specifici della bozza
        const updatedDraftData = {
            ...mappedData,
            isDraft: true,
            originalCatalogueId: draftData.originalCatalogueId,
            draftCreatedAt: draftData.draftCreatedAt,
            draftUpdatedAt: new Date().toISOString()
        };

        // Sanitizza i dati per Firestore
        const sanitizedData = sanitizeForFirestore(updatedDraftData);

        // Aggiorna il documento
        await updateDoc(draftRef, sanitizedData);

        return draftId;
    } catch (error) {
        console.error("Error updating draft:", error);
        throw error;
    }
}

/**
 * Salva un catalogo pubblicato come bozza
 * @param {string} originalCatalogueId - ID del catalogo pubblicato
 * @param {Object} formData - Dati del form
 * @returns {Promise<string>} - ID della bozza creata
 */
async function savePublishedAsDraft(originalCatalogueId, formData) {
    try {

        if (!originalCatalogueId) {
            throw new Error('Original catalogue ID is required');
        }

        // Ottieni i dati del catalogo originale
        const catalogueRef = doc(db, "Catalogues", originalCatalogueId);
        const catalogueSnap = await getDoc(catalogueRef);

        if (!catalogueSnap.exists()) {
            throw new Error(`Original catalogue with ID ${originalCatalogueId} not found`);
        }

        const catalogueData = catalogueSnap.data();

        // Prepara i dati del form aggiungendo l'ID del catalogo originale
        const readyFormData = {
            ...formData,
            originalCatalogueId: originalCatalogueId
        };

        // Prepara gli asset (carica immagini, ecc.)
        const processedFormData = await prepareAssetsAndSave(readyFormData);

        // Mappa i dati nella struttura JSON standard
        const mappedData = mapFormDataToCatalogue(processedFormData);

        // Rimuovi il riferimento alla bozza dal catalogo originale se esiste
        const { draftedCatalogRef, ...catalogueDataWithoutDraftRef } = catalogueData;

        // Crea i dati della bozza combinando il catalogo originale con le modifiche
        const draftData = {
            ...sanitizeForFirestore(catalogueDataWithoutDraftRef),

            // Campi base (usa il mapping centralizzato)
            nameCatalog: mappedData.nameCatalog || catalogueData.nameCatalog || 'Untitled Catalog',
            webDomain: mappedData.webDomain || catalogueData.webDomain || null,
            isPublic: mappedData.isPublic !== null ? mappedData.isPublic : catalogueData.isPublic,

            // ✅ CAMPI SEO PIATTI - USA SOLO IL MAPPING CENTRALIZZATO
            titleSeo: mappedData.titleSeo || catalogueData.titleSeo || null,
            list_keywords: mappedData.list_keywords || catalogueData.list_keywords || [],
            featuredImage: mappedData.featuredImage || catalogueData.featuredImage || null,
            metaDescription: mappedData.metaDescription || catalogueData.metaDescription || null,
            descriptionIt: mappedData.descriptionIt || catalogueData.descriptionIt || null,
            descriptionEn: mappedData.descriptionEn || catalogueData.descriptionEn || null,
            descriptionFr: mappedData.descriptionFr || catalogueData.descriptionFr || null,
            descriptionEs: mappedData.descriptionEs || catalogueData.descriptionEs || null,
            descriptionDe: mappedData.descriptionDe || catalogueData.descriptionDe || null,

            // Stile della pagina del catalogo
            catalogues_page_style: {
                ...catalogueData.catalogues_page_style,
                ...(mappedData.catalogues_page_style || {}),
            },

            // Banner e logo
            urlLogo: mappedData.urlLogo || catalogueData.urlLogo || null,

            // Campi specifici della bozza
            isDraft: true,
            originalCatalogueId: originalCatalogueId,
            draftCreatedAt: new Date().toISOString()
        };

        // Sanitizza i dati completi prima del salvataggio per rimuovere eventuali undefined
        const sanitizedDraftData = sanitizeForFirestore(draftData);

        // Salva il documento nella collezione DraftedCatalogues
        const docRef = await addDoc(collection(db, 'DraftedCatalogues'), sanitizedDraftData);
        const draftId = docRef.id;

        // Aggiorna la lista dei drafted catalogues nel profilo utente
        await updateUserDraftedCatalogues(draftId);

        // Aggiorna il riferimento alla bozza nel catalogo originale
        await updateDraftReference(originalCatalogueId, draftId);

        return draftId;
    } catch (error) {
        console.error("Error saving published catalog as draft:", error);
        throw error;
    }
}

/**
 * Salva una nuova bozza
 * @param {Object} formData - Dati del form
 * @returns {Promise<string>} - ID della bozza creata
 */
async function saveNewDraft(formData) {
    try {

        // Prepara gli asset (carica immagini, ecc.)
        const processedFormData = await prepareAssetsAndSave(formData);

        // Mappa i dati nella struttura JSON standard
        const mappedData = mapFormDataToCatalogue(processedFormData);

        // Aggiungi i campi specifici della bozza
        const draftData = {
            ...mappedData,
            isDraft: true,
            draftCreatedAt: new Date().toISOString()
        };

        // Sanitizza i dati per Firestore
        const sanitizedData = sanitizeForFirestore(draftData);

        // Salva il documento
        const docRef = await addDoc(collection(db, 'DraftedCatalogues'), sanitizedData);
        const draftId = docRef.id;

        // Aggiorna la lista dei drafted catalogues nel profilo utente
        await updateUserDraftedCatalogues(draftId);

        return draftId;
    } catch (error) {
        console.error("Error saving new draft:", error);
        throw error;
    }
}

/**
 * Salva un catalogo pubblicato
 * @param {Object} formData - Dati del form
 * @param {string} catalogueId - ID del catalogo esistente (per aggiornamenti)
 * @returns {Promise<string>} - ID del catalogo salvato
 */
async function savePublishedCatalogue(formData, catalogueId = null) {
    try {
        // Prepara gli asset (carica immagini, ecc.)
        const processedFormData = await prepareAssetsAndSave(formData);

        // Mappa i dati nella struttura JSON standard
        const mappedData = mapFormDataToCatalogue(processedFormData);

        // Sanitizza i dati per Firestore
        const sanitizedData = sanitizeForFirestore(mappedData);

        if (catalogueId) {
            // Aggiorna un catalogo esistente
            const catalogueRef = doc(db, 'Catalogues', catalogueId);
            await updateDoc(catalogueRef, sanitizedData);
            return catalogueId;
        } else {
            // Crea un nuovo catalogo
            const docRef = await addDoc(collection(db, 'Catalogues'), sanitizedData);
            return docRef.id;
        }
    } catch (error) {
        console.error("Error saving published catalogue:", error);
        throw error;
    }
}

/**
 * Ottiene un catalogo o una bozza dal database
 * @param {Object} options - Opzioni per il recupero
 * @param {string} options.catalogueId - ID del catalogo o della bozza
 * @param {boolean} options.isDraft - Se true, recupera dalla collezione DraftedCatalogues
 * @returns {Promise<Object|null>} - I dati del catalogo o della bozza
 */
export async function getCatalogueUnified(options) {
    const { catalogueId, isDraft = false } = options;

    try {
        const collectionName = isDraft ? 'DraftedCatalogues' : 'Catalogues';
        const docRef = doc(db, collectionName, catalogueId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: catalogueId, ...docSnap.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error(`Error getting ${isDraft ? 'draft' : 'catalogue'}:`, error);
        throw error;
    }
}



/**
 * Pubblica una bozza come catalogo
 * @param {string} draftId - ID della bozza
 * @returns {Promise<string>} - ID del catalogo pubblicato
 */
export async function publishDraftUnified(draftId) {
    try {

        // Ottieni i dati della bozza
        const draftData = await getCatalogueUnified({ catalogueId: draftId, isDraft: true });

        if (!draftData) {
            throw new Error(`Draft with ID ${draftId} not found`);
        }

        // Rimuovi i campi specifici delle bozze
        const { isDraft, draftCreatedAt, draftUpdatedAt, id, originalCatalogueId, licenseList, ...catalogueData } = draftData;

        let publishedCatalogueId;

        // Se è una modifica di un catalogo esistente, aggiorna il catalogo
        if (originalCatalogueId) {
            await updateDoc(doc(db, 'Catalogues', originalCatalogueId), catalogueData);

            // Rimuovi il riferimento alla bozza
            await removeDraftReference(originalCatalogueId);

            // Elimina la bozza
            await deleteDoc(doc(db, 'DraftedCatalogues', draftId));

            // Rimuovi la bozza dalla lista dell'utente
            await removeFromUserDraftedCatalogues(draftId);

            publishedCatalogueId = originalCatalogueId;
        } else {
            // Se è una nuova bozza, crea un nuovo catalogo
            const docRef = await addDoc(collection(db, 'Catalogues'), catalogueData);
            publishedCatalogueId = docRef.id;

            // Elimina la bozza
            await deleteDoc(doc(db, 'DraftedCatalogues', draftId));

            // Rimuovi la bozza dalla lista dell'utente
            await removeFromUserDraftedCatalogues(draftId);
        }

        // 🔥 ASSOCIA IL CATALOGO ALLE SOTTOSCRIZIONI SELEZIONATE
        if (licenseList) {
            // Converti licenseList in array se non lo è già
            const licenseArray = Array.isArray(licenseList) ? licenseList : [licenseList];

            if (licenseArray.length > 0) {
                await associateCatalogToLicenses(publishedCatalogueId, licenseArray);
            }
        }

        // 🆕 AGGIUNGI IL CATALOGO ALLA CATALOGSLIST DEL CLIENTREF
        await addCatalogToClientRef(publishedCatalogueId);

        return publishedCatalogueId;
    } catch (error) {
        console.error('Error publishing draft:', error);
        throw error;
    }
}

/**
 * Associa un catalogo alle sottoscrizioni selezionate
 * @param {string} catalogId - ID del catalogo pubblicato
 * @param {Array<string>} licenseIds - Array di ID delle licenze selezionate
 * @returns {Promise<void>}
 */
async function associateCatalogToLicenses(catalogId, licenseIds) {
    try {

        for (const licenseId of licenseIds) {

            // Ottieni la sottoscrizione
            const subscriptionRef = doc(db, 'ARShadesSubscriptions', licenseId);
            const subscriptionSnap = await getDoc(subscriptionRef);

            if (!subscriptionSnap.exists()) {
                console.warn(`License ${licenseId} not found, skipping association`);
                continue;
            }

            const subscriptionData = subscriptionSnap.data();

            // Prendi l'array esistente o crea uno nuovo
            const currentRefs = subscriptionData.list_catalogues?.list_catalogues_refs || [];

            // Aggiungi il catalogo se non è già presente
            if (!currentRefs.includes(catalogId)) {
                const updatedRefs = [...currentRefs, catalogId];

                // Aggiorna SOLO il campo list_catalogues_refs
                const updateData = {
                    'list_catalogues.list_catalogues_refs': updatedRefs
                };

                await updateDoc(subscriptionRef, updateData);

                // Verifica immediata dell'aggiornamento
                const verifySnap = await getDoc(subscriptionRef);
                if (verifySnap.exists()) {
                    const verifyData = verifySnap.data();
                }
            }
        }
    } catch (error) {
        console.error('❌ ERROR in associateCatalogToLicenses:', error);
        throw error;
    }
}

/**
 * Rimuove una bozza dalla lista dell'utente
 * @param {string} draftId - L'ID della bozza
 * @returns {Promise<void>}
 */
async function removeFromUserDraftedCatalogues(draftId) {
    try {
        let email = secureGetItem('email');

        if (email) {
            email = email.toLowerCase();
            const profileRef = doc(db, 'Profiles', email);
            const profileSnap = await getDoc(profileRef);

            if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                let listDrafted = Array.isArray(profileData.list_drafted_catalogues)
                    ? profileData.list_drafted_catalogues
                    : [];

                // Rimuovi la bozza dalla lista
                listDrafted = listDrafted.filter(id => id !== draftId);

                await setDoc(profileRef, { list_drafted_catalogues: listDrafted }, { merge: true });
            }
        }
    } catch (error) {
        console.error('Error removing draft from user list:', error);
    }
}

/**
 * Ottiene tutte le bozze dell'utente corrente
 * @returns {Promise<Array>} - Array di bozze
 */
export async function getUserDraftedCataloguesUnified() {
    try {
        let email = secureGetItem('email');

        if (!email) {
            return [];
        }

        email = email.toLowerCase();
        const profileRef = doc(db, 'Profiles', email);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
            return [];
        }

        const profileData = profileSnap.data();
        const listDrafted = Array.isArray(profileData.list_drafted_catalogues)
            ? profileData.list_drafted_catalogues
            : [];

        if (listDrafted.length === 0) {
            return [];
        }

        // Ottieni i dati di tutte le bozze
        const drafts = [];

        for (const draftId of listDrafted) {
            const draft = await getCatalogueUnified({ catalogueId: draftId, isDraft: true });

            if (draft) {
                drafts.push(draft);
            }
        }

        return drafts;
    } catch (error) {
        console.error('Error getting user drafted catalogues:', error);
        return [];
    }
}
